import React, { useState, useEffect } from 'react';
import api from '../api';
import { FaSearch, FaPlus, FaMinus, FaEdit, FaTrash, FaBoxOpen, FaTags } from 'react-icons/fa';
import CategoriasEmbalagemModal from './CategoriasEmbalagemModal';
import PageLayout from './PageLayout';
import ExemploBadge from './ExemploBadge';
import { theme, t } from '../theme';
import { norm } from '../utils';
import { useConfirm } from './ConfirmProvider';
import Tooltip from './Tooltip';
import useIsMounted from '../hooks/useIsMounted';
import useViewMode from '../hooks/useViewMode';
import ViewToggle from './ViewToggle';

const tableActionBtn = (color, bg) => ({
  width: 32, height: 32, borderRadius: 8, border: 'none',
  background: bg, color, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
});

const Embalagens = () => {
  const mounted = useIsMounted();
  const [embalagens, setEmbalagens] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [busca, setBusca] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [loading, setLoading] = useState(true);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [embalagemEditando, setEmbalagemEditando] = useState(null);
  const [mostrarModalEstoque, setMostrarModalEstoque] = useState(null);
  const [mostrarCategorias, setMostrarCategorias] = useState(false);
  const confirm = useConfirm();
  const { mode: vmMode, setMode: setVmMode, effective: vmEffective } = useViewMode('view:embalagens');

  useEffect(() => { carregarDados(); }, []);

  const queroAbrirNovoRef = React.useRef(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('novo') === '1'
  );
  useEffect(() => {
    if (loading) return;
    if (!queroAbrirNovoRef.current) return;
    queroAbrirNovoRef.current = false;
    setEmbalagemEditando(null);
    setMostrarModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [resEmb, resCat] = await Promise.all([
        api.get('/api/embalagens'),
        api.get('/api/embalagens/categorias'),
      ]);
      if (!mounted.current) return;
      setEmbalagens(resEmb.data);
      setCategorias(resCat.data);
    } catch {} finally { if (mounted.current) setLoading(false); }
  };

  const handleDelete = async (id, nome) => {
    if (!await confirm(`Excluir "${nome}"?`, { danger: true, confirmText: 'Excluir' })) return;
    const backup = [...embalagens];
    setEmbalagens(prev => prev.filter(e => e.id !== id));
    try {
      await api.delete(`/api/embalagens/${id}`);
    } catch (error) {
      setEmbalagens(backup);
      const data = error.response?.data;
      if (data?.fichas) {
        alert(`${data.mensagem}\n\n${data.fichas.join('\n')}`);
      } else if (data?.mensagem || data?.erro) {
        alert(data.mensagem || data.erro);
      }
    }
  };

  const handleMovEstoque = async (id, tipo, quantidade) => {
    if (quantidade <= 0) return;
    const delta = tipo === 'entrada' ? quantidade : -quantidade;
    setEmbalagens(prev => prev.map(e => e.id === id ? { ...e, estoque_atual: Math.max(0, e.estoque_atual + delta) } : e));
    setMostrarModalEstoque(null);
    try { await api.post(`/api/embalagens/${id}/${tipo}`, { quantidade }); } catch { carregarDados(); }
  };

  const filtradas = embalagens.filter(e => {
    const matchBusca = norm(e.nome).includes(norm(busca));
    const matchCat = !filtroCategoria || e.categoria === filtroCategoria;
    return matchBusca && matchCat;
  });

  const formatarMoeda = (v) => `R$ ${(v || 0).toFixed(2).replace('.', ',')}`;

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}>Carregando...</div>;

  return (
    <PageLayout title="Embalagens" maxWidth={1000}
      action={<div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => setMostrarCategorias(true)} style={t.btnSecondary}><FaTags /> Categorias</button>
        <button onClick={() => { setEmbalagemEditando(null); setMostrarModal(true); }} style={t.btnPrimary}><FaPlus /> Nova Embalagem</button>
      </div>}>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <FaSearch style={{ position: 'absolute', left: 12, top: 11, color: '#aaa' }} />
          <input placeholder="Buscar..." value={busca} onChange={e => setBusca(e.target.value)} style={{ ...t.input, paddingLeft: 36 }} />
        </div>
        <select value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)} style={{ ...t.select, width: 'auto', minWidth: 180 }}>
          <option value="">Todas categorias</option>
          {categorias.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <ViewToggle mode={vmMode} setMode={setVmMode} />
      </div>

      {filtradas.length === 0 ? (
        <div style={{ textAlign: 'center', color: theme.colors.textMuted, padding: 60 }}>
          <FaBoxOpen size={40} style={{ marginBottom: 12, color: theme.colors.border }} />
          <p>Nenhuma embalagem encontrada.</p>
        </div>
      ) : vmEffective === 'tabela' ? (
        <div className="table-responsive" style={{ background: '#fff', borderRadius: 12, padding: 8, border: '1px solid #f0f0f0', overflowX: 'auto' }}>
          <table className="table table-sm align-middle" style={{ marginBottom: 0, width: '100%' }}>
            <thead style={{ background: '#faf7f5' }}>
              <tr>
                <th style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', fontWeight: 600, letterSpacing: '0.04em' }}>Nome</th>
                <th style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', fontWeight: 600, letterSpacing: '0.04em' }}>Categoria</th>
                <th className="text-end" style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', fontWeight: 600, letterSpacing: '0.04em' }}>Custo</th>
                <th className="text-end" style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', fontWeight: 600, letterSpacing: '0.04em' }}>Mínimo</th>
                <th className="text-end" style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', fontWeight: 600, letterSpacing: '0.04em' }}>Estoque</th>
                <th className="text-center" style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', fontWeight: 600, letterSpacing: '0.04em' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map(e => {
                const isControlado = e.controlar_estoque;
                const isStockCritical = isControlado && e.estoque_atual <= 0;
                const isStockLow = isControlado && e.estoque_atual > 0 && e.estoque_minimo > 0 && e.estoque_atual <= e.estoque_minimo;
                const stockBg = !isControlado ? '#f1f3f5' : isStockCritical ? '#fdecea' : isStockLow ? '#fff7e6' : '#e8f5e9';
                const stockColor = !isControlado ? '#888' : isStockCritical ? '#e74c3c' : isStockLow ? '#d97706' : '#2e7d32';
                return (
                  <tr key={e.id}>
                    <td><strong>{e.nome}</strong><ExemploBadge origem={e.origem} />{!isControlado && <div style={{ fontSize: 11, color: '#aaa' }}>sem controle de estoque</div>}</td>
                    <td>{e.categoria ? <span style={t.badge(theme.colors.info, theme.colors.infoLight)}>{e.categoria}</span> : '-'}</td>
                    <td className="text-end" style={{ fontWeight: 600 }}>{formatarMoeda(e.custo_unitario)}</td>
                    <td className="text-end">{isControlado ? e.estoque_minimo : '—'}</td>
                    <td className="text-end">
                      <span style={{ background: stockBg, color: stockColor, padding: '2px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
                        {isControlado ? e.estoque_atual : '—'}
                      </span>
                    </td>
                    <td className="text-center">
                      <div style={{ display: 'inline-flex', gap: 4 }}>
                        {isControlado && (
                          <>
                            <button onClick={() => setMostrarModalEstoque({ embalagem: e, tipo: 'entrada' })} title="Entrada" style={tableActionBtn(theme.colors.success, '#e8f5e9')}><FaPlus size={14} /></button>
                            <button onClick={() => setMostrarModalEstoque({ embalagem: e, tipo: 'saida' })} title="Saída" style={tableActionBtn(theme.colors.danger, '#fdecea')}><FaMinus size={14} /></button>
                          </>
                        )}
                        <button onClick={() => { setEmbalagemEditando(e); setMostrarModal(true); }} title="Editar" style={tableActionBtn(theme.colors.info, '#f1f3f5')}><FaEdit size={14} /></button>
                        <button onClick={() => handleDelete(e.id, e.nome)} title="Excluir" style={tableActionBtn(theme.colors.danger, '#fdecea')}><FaTrash size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
          {filtradas.map(e => {
            const isControlado = e.controlar_estoque;
            const isStockCritical = isControlado && e.estoque_atual <= 0;
            const isStockLow = isControlado && e.estoque_atual > 0 && e.estoque_minimo > 0 && e.estoque_atual <= e.estoque_minimo;
            const stripeColor = !isControlado ? theme.colors.textMuted : isStockCritical ? theme.colors.danger : isStockLow ? theme.colors.warning : theme.colors.success;
            const stockColor = !isControlado ? theme.colors.textMuted : isStockCritical ? theme.colors.danger : isStockLow ? theme.colors.warning : theme.colors.success;
            return (
              <div key={e.id} style={{ background: '#fff', borderRadius: 14, border: `1px solid ${theme.colors.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ height: 4, background: stripeColor }} />
                <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 600, color: theme.colors.text, fontSize: 15, lineHeight: 1.2 }}>{e.nome}<ExemploBadge origem={e.origem} /></div>
                      {!isControlado && <div style={{ fontSize: 11, color: theme.colors.textMuted, marginTop: 2 }}>sem controle de estoque</div>}
                      {e.categoria && (
                        <div style={{ marginTop: 6 }}>
                          <span style={t.badge(theme.colors.info, theme.colors.infoLight)}>{e.categoria}</span>
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: theme.colors.textMuted, fontWeight: 600 }}>Custo</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>{formatarMoeda(e.custo_unitario)}</div>
                    </div>
                  </div>
                  {/* Numeros e acoes na MESMA linha, sem divisoria. Antes eram tres faixas
                      (grade 2x2, borda, botoes) para dois numeros de um digito — o card de
                      embalagem sem controle de estoque chegava a mostrar so tracos. */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 2 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 12px', fontSize: 12, minWidth: 0 }}>
                      {isControlado && [['Mínimo', String(e.estoque_minimo ?? '-'), theme.colors.textSecondary],
                        ['Estoque', String(e.estoque_atual ?? '-'), stockColor]].map(([rot, val, cor]) => (
                        <span key={rot} style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: theme.colors.textMuted, fontWeight: 600, marginRight: 4 }}>{rot}</span>
                          <span style={{ color: cor, fontWeight: 700 }}>{val}</span>
                        </span>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    {isControlado && (
                      <>
                        <button onClick={() => setMostrarModalEstoque({ embalagem: e, tipo: 'entrada' })} title="Entrada"
                          style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#e8f5e9', color: theme.colors.success, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaPlus size={13} /></button>
                        <button onClick={() => setMostrarModalEstoque({ embalagem: e, tipo: 'saida' })} title="Saída"
                          style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#fdecea', color: theme.colors.danger, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaMinus size={13} /></button>
                      </>
                    )}
                    <button onClick={() => { setEmbalagemEditando(e); setMostrarModal(true); }} title="Editar"
                      style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#f1f3f5', color: theme.colors.info, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaEdit size={16} /></button>
                    <button onClick={() => handleDelete(e.id, e.nome)} title="Excluir"
                      style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#fdecea', color: theme.colors.danger, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaTrash size={16} /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {mostrarModal && (
        <EmbalagemModal embalagem={embalagemEditando} categorias={categorias} onClose={() => setMostrarModal(false)} onSave={(data, isEdit) => {
          setMostrarModal(false);
          if (isEdit) setEmbalagens(prev => prev.map(e => e.id === data.id ? data : e));
          else setEmbalagens(prev => [...prev, data]);
          if (data.categoria && !categorias.includes(data.categoria)) setCategorias(prev => [...prev, data.categoria]);
        }} />
      )}

      {mostrarModalEstoque && (
        <EstoqueModal embalagem={mostrarModalEstoque.embalagem} tipo={mostrarModalEstoque.tipo} onClose={() => setMostrarModalEstoque(null)} onSave={(qtd) => handleMovEstoque(mostrarModalEstoque.embalagem.id, mostrarModalEstoque.tipo, qtd)} />
      )}

      {mostrarCategorias && (
        <CategoriasEmbalagemModal fecharModal={() => setMostrarCategorias(false)} recarregar={carregarDados} />
      )}
    </PageLayout>
  );
};

const EmbalagemModal = ({ embalagem, categorias, onClose, onSave }) => {
  const [nome, setNome] = useState(embalagem?.nome || '');
  const [custoUnitario, setCustoUnitario] = useState(embalagem?.custo_unitario ? parseFloat(embalagem.custo_unitario).toFixed(2).replace('.', ',') : '');
  const [categoria, setCategoria] = useState(embalagem?.categoria || '');
  const [novaCategoria, setNovaCategoria] = useState('');
  const [controlarEstoque, setControlarEstoque] = useState(embalagem?.controlar_estoque || false);
  const [estoqueMinimo, setEstoqueMinimo] = useState(embalagem?.estoque_minimo?.toString() || '0');
  const [erro, setErro] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro(null);
    const cat = novaCategoria.trim() || (categoria === '__nova__' ? '' : categoria);
    const payload = {
      nome, custo_unitario: parseFloat(custoUnitario.replace(',', '.')),
      categoria: cat, controlar_estoque: controlarEstoque,
      estoque_minimo: controlarEstoque ? parseInt(estoqueMinimo) || 0 : 0,
    };
    try {
      let res;
      if (embalagem?.id) { res = await api.put(`/api/embalagens/${embalagem.id}`, payload); }
      else { res = await api.post('/api/embalagens', payload); }
      onSave(res.data, !!embalagem?.id);
    } catch (err) { setErro(err.response?.data?.erro || 'Erro ao salvar.'); }
  };

  return (
    <div style={t.overlay} className="modal-overlay-mobile">
      <div style={{ ...t.modal, maxWidth: 420 }} className="modal-content-sheet">
        <h3 style={{ marginBottom: 16 }}>{embalagem ? 'Editar Embalagem' : 'Nova Embalagem'}</h3>
        {erro && <div style={t.error}>{erro}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}><label style={t.label}>Nome *</label><input value={nome} onChange={e => setNome(e.target.value)} style={t.input} required /></div>
          <div style={{ marginBottom: 14 }}><label style={t.label}>Custo Unitário (R$) *</label><input value={custoUnitario} onChange={e => { const num = e.target.value.replace(/\D/g, ''); if (!num) { setCustoUnitario(''); return; } setCustoUnitario((parseInt(num, 10) / 100).toFixed(2).replace('.', ',')); }} placeholder="0,00" style={t.input} required /></div>
          <div style={{ marginBottom: 14 }}>
            <label style={t.label}>Categoria</label>
            <select value={categoria} onChange={e => { setCategoria(e.target.value); setNovaCategoria(''); }} style={t.select}>
              <option value="">Sem categoria</option>
              {categorias.map(c => <option key={c} value={c}>{c}</option>)}
              <option value="__nova__">+ Nova categoria...</option>
            </select>
            {categoria === '__nova__' && (
              <input placeholder="Nome da nova categoria" value={novaCategoria} onChange={e => setNovaCategoria(e.target.value)} style={{ ...t.input, marginTop: 8 }} autoFocus />
            )}
          </div>
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" checked={controlarEstoque} onChange={e => setControlarEstoque(e.target.checked)} style={{ width: 18, height: 18 }} />
            <label style={{ ...t.label, margin: 0 }}>Controlar estoque</label>
            <Tooltip text="A cada pedido confirmado, a embalagem é automaticamente deduzida do estoque. Você define um estoque mínimo e recebe alertas quando ele fica baixo, além de poder registrar entradas e saídas manuais.">
              <span
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 18, height: 18, borderRadius: '50%',
                  background: theme.colors.bgSection || '#F3F4F6', color: theme.colors.textMuted,
                  fontSize: 11, fontWeight: 700, cursor: 'help', userSelect: 'none',
                }}
              >?</span>
            </Tooltip>
          </div>
          {controlarEstoque && (
            <div style={{
              marginBottom: 14, padding: 10, background: theme.colors.bgSection || '#F9FAFB',
              border: `1px solid ${theme.colors.border}`, borderRadius: 8,
              fontSize: 12, color: theme.colors.textSecondary, lineHeight: 1.5,
            }}>
              💡 <strong>Como funciona:</strong> a cada pedido confirmado que use esta embalagem, a quantidade é deduzida automaticamente do estoque. Você também pode registrar entradas e saídas manuais e recebe alertas por e-mail quando o estoque fica abaixo do mínimo definido.
            </div>
          )}
          {controlarEstoque && (
            <div style={{ marginBottom: 14 }}><label style={t.label}>Estoque Mínimo</label><input type="number" value={estoqueMinimo} onChange={e => setEstoqueMinimo(e.target.value)} style={t.input} min="0" /></div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            <button type="submit" style={t.btnPrimary}>Salvar</button>
            <button type="button" onClick={onClose} style={t.btnSecondary}>Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EstoqueModal = ({ embalagem, tipo, onClose, onSave }) => {
  const [quantidade, setQuantidade] = useState('');
  const isEntrada = tipo === 'entrada';
  return (
    <div style={t.overlay}>
      <div style={{ ...t.modal, maxWidth: 340 }}>
        <h3 style={{ marginBottom: 4 }}>{isEntrada ? 'Entrada' : 'Saída'} de Estoque</h3>
        <p style={{ color: theme.colors.textSecondary, fontSize: 14, marginBottom: 16 }}>{embalagem.nome}</p>
        <p style={{ fontSize: 13, color: theme.colors.text, marginBottom: 12 }}>Estoque atual: <strong>{embalagem.estoque_atual}</strong></p>
        <div style={{ marginBottom: 14 }}><label style={t.label}>Quantidade</label><input type="number" min="1" value={quantidade} onChange={e => setQuantidade(e.target.value)} style={t.input} autoFocus /></div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={() => onSave(parseInt(quantidade) || 0)} style={{ ...t.btnPrimary, background: isEntrada ? theme.colors.success : theme.colors.danger }} disabled={!quantidade || parseInt(quantidade) <= 0}>
            {isEntrada ? 'Registrar Entrada' : 'Registrar Saída'}
          </button>
          <button onClick={onClose} style={t.btnSecondary}>Cancelar</button>
        </div>
      </div>
    </div>
  );
};

export default Embalagens;
